import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../../services/apiService';
import { Button, EmptyState } from '../../ui';
import IronSightMark from '../../shared/IronSightMark';
import {
    BRIEF_SECTIONS, SERVICE_STEP_DURATIONS, SERVICE_THINKING_STEPS, cccLines, fmtPartPrice, fmtServiceDate,
    BRIEF_ERROR, fmtServiceDateShort, isPlannedParts, parseBrief, partsPillText, serviceUnitMeta, sortWorkOrders, workOrderOneLiner,
} from '../../../utils/serviceHistory';
import { RefreshIcon } from '../icons';
import StepLoader from './serviceThinking';
import { tabCtxShape } from './tabContext';
import './ServiceTabV2.css';

const BRIEF_IDLE_HINT = 'Generate an AI pre-call brief — condition, talking points, and what to watch out for.';
const CHAT_ERROR = 'Could not get an answer. Please try again.';

function BriefBody({ summary }) {
    return (
        <div className="u2-svc-brief">
            {parseBrief(summary).map(({ heading, content, lines, bullets }) => (
                <div key={heading || 'raw'} className={`u2-svc-bs${heading === 'WATCH OUT' ? ' is-watch' : ''}`}>
                    {heading && <h4>{BRIEF_SECTIONS[heading]?.label || heading}</h4>}
                    {bullets ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <ul>{lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
                    ) : <p>{content}</p>}
                </div>
            ))}
        </div>
    );
}
BriefBody.propTypes = { summary: PropTypes.string.isRequired };

function Chat({ chat, setChat, stockNumber, summary, unitMeta, serviceHistory }) {
    const [input, setInput] = useState('');
    const bottomRef = useRef(null);
    const { messages, asking } = chat;

    const ask = async () => {
        const q = input.trim();
        if (!q || asking) return;
        const next = [...messages, { role: 'user', text: q }];
        setChat({ messages: next, asking: true });
        setInput('');
        let answer;
        try {
            const data = await apiService.askServiceHistory(stockNumber, {
                question: q, summary, history: messages, unitMeta: unitMeta || {}, serviceHistory: serviceHistory || null,
            });
            answer = data.answer;
        } catch {
            answer = CHAT_ERROR;
        }
        setChat({ messages: [...next, { role: 'assistant', text: answer }], asking: false });
        setTimeout(() => bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' }), 50);
    };

    return (
        <div className="u2-svc-chat">
            <span className="u2-eyebrow">Ask a follow-up</span>
            {messages.length > 0 && (
                <div className="u2-svc-msgs" aria-live="polite">
                    {messages.map((m, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} className={`u2-svc-msg is-${m.role === 'user' ? 'u' : 'a'}`}>{m.text}</div>
                    ))}
                    {asking && <div className="u2-svc-msg is-a is-typing" aria-label="IronSight is answering">…</div>}
                    <div ref={bottomRef} />
                </div>
            )}
            <div className="u2-svc-chat-in">
                <label className="v2-sr" htmlFor={`svc-q-${stockNumber}`}>Question about service history</label>
                <textarea
                    id={`svc-q-${stockNumber}`}
                    rows={2}
                    placeholder='e.g. "Customer is pushing back on price — what should I say?"'
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                    disabled={asking}
                />
                <Button variant="primary" onClick={ask} disabled={asking || !input.trim()}>Ask</Button>
            </div>
        </div>
    );
}
Chat.propTypes = {
    chat: PropTypes.object.isRequired,
    setChat: PropTypes.func.isRequired,
    stockNumber: PropTypes.string,
    summary: PropTypes.string,
    unitMeta: PropTypes.object,
    serviceHistory: PropTypes.object,
};

function WorkOrderDetail({ wo }) {
    if (!wo) return <div className="u2-card"><p className="u2-note">Select a work order to view details.</p></div>;
    const hasCCC = wo.ccc && wo.ccc.length > 0;
    const hasParts = wo.parts && wo.parts.length > 0;
    return (
        <div className="u2-card" aria-label="Work order detail">
            <div className="u2-c-head">
                <h3>RO #{wo.ro_number}</h3>
                <span className="u2-sub">{fmtServiceDate(wo.closed_date)}</span>
                {wo.ro_closed_ind === 'Y' && <span className="u2-right"><span className="u2-svc-closed">Closed</span></span>}
            </div>
            {hasCCC && wo.ccc.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`${item.job_code}-${i}`} className="u2-svc-job">
                    <b className="u2-svc-job-code">{item.job_code || '—'}</b>
                    <dl className="u2-svc-ccc">
                        {cccLines(item).map((l) => (
                            <div key={l.kind} className={`is-${l.kind}`}>
                                <dt>{l.label}</dt>
                                <dd>{l.text}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            ))}
            {hasParts && (
                <div className="u2-svc-parts">
                    <div className="u2-c-head"><h4>Parts Used</h4><span className="u2-svc-count">{wo.parts.length}</span></div>
                    <div className="u2-tw">
                        <table className="u2-tt">
                            <thead><tr><th>Part</th><th>Part no.</th><th className="r">List price</th></tr></thead>
                            <tbody>
                                {wo.parts.map((p, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <tr key={`${p.part_no}-${i}`}>
                                        <td className="u2-svc-wrap">{p.part_desc || p.part_no}</td>
                                        <td>{p.part_no}</td>
                                        <td className="r">{fmtPartPrice(p.list_price) || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {!hasCCC && !hasParts && <p className="u2-note">No job lines or parts recorded for this work order.</p>}
        </div>
    );
}
WorkOrderDetail.propTypes = { wo: PropTypes.object };

/**
 * Service History on the full unit page (P-UNIT-072..078). The page keeps the
 * tab mounted while another tab shows, so the brief, the chat and the
 * selected work order survive tab switches; it remounts the tab for the next
 * unit, so none of them carry over (U2). The brief is generated on demand.
 */
export default function ServiceTabV2({ ctx }) {
    const { unit, detail } = ctx;
    const { serviceHistory, serviceHistoryLoading, refreshServiceHistory } = detail;
    const stockNumber = unit.StockNumber;
    const [selectedIndex, setSelectedIndex] = useState(0);
    // Brief: idle | loading | done | error (P-UNIT-076); chat after a brief.
    const [brief, setBrief] = useState({ state: 'idle', summary: '', error: '' });
    const [chat, setChat] = useState({ messages: [], asking: false });
    const unitMeta = serviceUnitMeta(unit, detail.mfData?.options || []);

    const generate = async () => {
        setBrief({ state: 'loading', summary: '', error: '' });
        setChat({ messages: [], asking: false });
        try {
            const data = await apiService.getServiceHistorySummary(stockNumber, unitMeta);
            setBrief({ state: 'done', summary: data.summary, error: '' });
        } catch (err) {
            setBrief({ state: 'error', summary: '', error: BRIEF_ERROR });
        }
    };

    if (serviceHistoryLoading) {
        return (
            <StepLoader
                title="Loading service history"
                unit={unit}
                steps={SERVICE_THINKING_STEPS}
                durations={SERVICE_STEP_DURATIONS}
            />
        );
    }

    if (!serviceHistory || serviceHistory.total_ros === 0) {
        return <EmptyState message="No workshop service records found for this unit." />;
    }

    const sorted = sortWorkOrders(serviceHistory.work_orders);
    const selected = sorted[selectedIndex] || sorted[0];

    return (
        <div className="u2-svc">
            <div className="u2-card u2-svc-list">
                <div className="u2-c-head">
                    <h3>Work orders</h3>
                    <span className="u2-sub">
                        {serviceHistory.total_ros} work order{serviceHistory.total_ros !== 1 ? 's' : ''}
                        {serviceHistory.total_parts > 0 && <> · {serviceHistory.total_parts} parts</>}
                    </span>
                    <span className="u2-right">
                        <button
                            type="button"
                            className="u2-ib"
                            onClick={refreshServiceHistory}
                            title="Refresh service history"
                            aria-label="Refresh service history"
                        >
                            <RefreshIcon />
                        </button>
                    </span>
                </div>
                {serviceHistory.cached_at && <span className="u2-sub">Loaded {fmtServiceDate(serviceHistory.cached_at)}</span>}
                <ul className="u2-svc-wo">
                    {sorted.map((wo, i) => {
                        const parts = partsPillText(wo);
                        return (
                            <li key={wo.ro_number}>
                                <button type="button" aria-current={i === selectedIndex ? 'true' : undefined} onClick={() => setSelectedIndex(i)}>
                                    <span className="u2-svc-date">{fmtServiceDateShort(wo.closed_date)}</span>
                                    <b>{workOrderOneLiner(wo)}</b>
                                    <span className="u2-svc-meta">
                                        {parts ? <span className="u2-svc-pill">{parts}</span> : <span className="u2-quiet">no parts</span>}
                                        {isPlannedParts(wo) && <span className="u2-svc-pill is-planned">planned</span>}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className="u2-col">
                <div className="u2-card u2-card--ai">
                    <div className="u2-c-head">
                        <span className="u2-is-mark"><IronSightMark /> AI service intelligence</span>
                        <h3>Pre-call brief</h3>
                        {brief.state !== 'loading' && (
                            <span className="u2-right">
                                <Button size="sm" onClick={generate}>{brief.state === 'done' ? 'Regenerate' : 'Generate brief'}</Button>
                            </span>
                        )}
                    </div>
                    {brief.state === 'idle' && <p className="u2-note">{BRIEF_IDLE_HINT}</p>}
                    {brief.state === 'loading' && (
                        <p className="u2-note" role="status"><span className="u2-steps-pulse" aria-hidden="true" /> Preparing brief for {stockNumber}...</p>
                    )}
                    {brief.state === 'done' && (
                        <>
                            <BriefBody summary={brief.summary} />
                            <Chat
                                chat={chat}
                                setChat={setChat}
                                stockNumber={stockNumber}
                                summary={brief.summary}
                                unitMeta={unitMeta}
                                serviceHistory={serviceHistory}
                            />
                        </>
                    )}
                    {brief.state === 'error' && (
                        <div className="u2-svc-brief-err" role="alert">
                            <p className="u2-note u2-neg">{brief.error}</p>
                            <Button size="sm" onClick={generate}>Retry</Button>
                        </div>
                    )}
                </div>
                <WorkOrderDetail wo={selected} />
            </div>
        </div>
    );
}

ServiceTabV2.propTypes = { ctx: tabCtxShape.isRequired };
